import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatDate, formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import { CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, GitForkIcon as GitFork, EyeIcon as Eye, ArrowsCounterClockwiseIcon as ArrowsCounterClockwise } from "@phosphor-icons/react";
import PedigreeAdditionPreview from "@/components/PedigreeAdditionPreview";
import RequestRevisionsDialog from "@/components/RequestRevisionsDialog";

const TABS = [
  { id: "pending_admin_approve", label: "Pending" },
  { id: "revisions_requested", label: "Revisions requested" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

export default function AdminPedigreeAdditions() {
  const [status, setStatus] = useState("pending_admin_approve");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [revisionsTarget, setRevisionsTarget] = useState(null);

  const load = (s = status) => {
    setLoading(true);
    api
      .get(`/admin/pedigree-additions?status=${s}`)
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const decide = async (id, kind) => {
    let reason = "";
    if (kind === "reject") {
      reason = window.prompt("Reason for rejection:", "") || "";
    }
    setBusy(id);
    try {
      await api.post(`/admin/pedigree-additions/${id}/${kind}`, { reason });
      toast.success(kind === "approve" ? "Addition approved" : "Addition rejected");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight" data-testid="admin-pedigree-heading">
            Pedigree additions
          </h1>
          <p className="text-[#6B6B6B] mt-2">
            Review owner-submitted ancestors. Approving creates pedigree-only
            dog records linked via <code>sire_id</code> / <code>dam_id</code>
            — no registration numbers, but they appear on the pedigree tree.
          </p>
        </div>

        <Tabs value={status} onValueChange={setStatus}>
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-white border border-[#E5E5E5] rounded-full p-1">
              {TABS.map((t) => (
                <TabsTrigger
                  key={t.id}
                  value={t.id}
                  className="rounded-full px-4 text-xs"
                  data-testid={`admin-pedigree-tab-${t.id}`}
                >
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {TABS.map((t) => (
            <TabsContent key={t.id} value={t.id}>
              {loading ? (
                <div className="text-[#6B6B6B] mt-4">Loading…</div>
              ) : rows.length === 0 ? (
                <div className="bg-white border border-dashed border-[#E5E5E5] rounded-xl p-12 text-center mt-4" data-testid="admin-pedigree-empty">
                  <GitFork size={40} weight="duotone" className="text-[#6B6B6B]/40 mx-auto mb-3" />
                  <p className="font-spectral text-xl">No additions in this tab</p>
                </div>
              ) : (
                <div className="space-y-3 mt-4" data-testid="admin-pedigree-list">
                  {rows.map((row) => (
                    <div
                      key={row.id}
                      className="bg-white border border-[#E5E5E5] rounded-xl p-5"
                      data-testid={`admin-pedigree-row-${row.id}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Link
                              to={`/dogs/${row.attached_dog_id}`}
                              className="font-spectral text-xl tracking-tight hover:text-crimson transition-colors"
                            >
                              {row.attached_dog_name}
                            </Link>
                            <Badge variant="outline" className="text-xs">
                              {row.entries.length} ancestor{row.entries.length !== 1 ? "s" : ""}
                            </Badge>
                          </div>
                          <p className="text-xs text-[#6B6B6B] mt-1">
                            Submitted by {row.submitted_by_name || "—"} · {formatDate(row.created_at)}
                            {row.approved_at ? ` · Approved ${formatDate(row.approved_at)}` : ""}
                            {row.rejected_at ? ` · Rejected ${formatDate(row.rejected_at)}` : ""}
                          </p>
                          {row.rejection_reason && (
                            <p className="text-sm text-[#A12125] mt-1">
                              Reason: {row.rejection_reason}
                            </p>
                          )}
                          {row.revision_notes && (
                            <div
                              className="mt-2 bg-[#FBF7EC] border border-[#E5D08A] rounded-lg px-3 py-2"
                              data-testid={`admin-pedigree-revision-notes-${row.id}`}
                            >
                              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A6A00]">
                                Revisions requested
                                {row.revisions_requested_by_name && (
                                  <span className="font-normal normal-case ml-1">
                                    by {row.revisions_requested_by_name}
                                  </span>
                                )}
                              </p>
                              <p className="text-sm text-ink mt-1 whitespace-pre-wrap">
                                {row.revision_notes}
                              </p>
                            </div>
                          )}
                          <div className="mt-4 space-y-2 text-sm">
                            {row.entries.map((e, idx) => (
                              <div
                                key={e.entry_id || idx}
                                className="bg-[#FAF7EE] border border-[#E5D08A]/50 rounded-lg p-3"
                              >
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-bold text-[#8A6A00] uppercase tracking-widest text-xs">
                                    {e.relation}
                                  </span>
                                  <strong className="font-spectral text-base">
                                    {e.name}
                                  </strong>
                                  {e.sex && (
                                    <Badge variant="outline" className="text-xs">
                                      {e.sex}
                                    </Badge>
                                  )}
                                  {e.parent_entry_id && (
                                    <span className="text-xs text-[#6B6B6B]">
                                      (parent of: {e.parent_entry_id})
                                    </span>
                                  )}
                                </div>
                                <div className="mt-1 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-[#555]">
                                  {e.dob && <span>DOB: {e.dob}</span>}
                                  {e.color && <span>Color: {e.color}</span>}
                                  {e.breed && <span>Breed: {e.breed}</span>}
                                  {e.microchip && <span>Chip: {e.microchip}</span>}
                                  {e.external_registration && (
                                    <span>Ext reg: {e.external_registration}</span>
                                  )}
                                </div>
                                {e.notes && (
                                  <p className="text-xs italic text-[#6B6B6B] mt-2">
                                    Note: {e.notes}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        {row.status === "pending_admin_approve" && (
                          <div className="flex flex-col items-stretch gap-2 shrink-0">
                            <Button
                              variant="outline"
                              onClick={() => setPreviewId(row.id)}
                              className="rounded-full border-[#C9A227]/40 text-[#8A6A00] bg-[#FBF7EC] hover:bg-[#F3E8C5]"
                              data-testid={`admin-pedigree-preview-${row.id}`}
                            >
                              <Eye size={14} className="mr-1" weight="duotone" />
                              Preview pedigree
                            </Button>
                            <Button
                              disabled={busy === row.id}
                              onClick={() => decide(row.id, "approve")}
                              className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                              data-testid={`admin-pedigree-approve-${row.id}`}
                            >
                              <CheckCircle size={14} className="mr-1" weight="duotone" />
                              Approve
                            </Button>
                            <Button
                              disabled={busy === row.id}
                              onClick={() => setRevisionsTarget(row)}
                              variant="outline"
                              className="rounded-full border-[#C9A227]/40 text-[#8A6A00] hover:bg-[#FBF7EC]"
                              data-testid={`admin-pedigree-revisions-${row.id}`}
                            >
                              <ArrowsCounterClockwise size={14} className="mr-1" weight="duotone" />
                              Send back
                            </Button>
                            <Button
                              disabled={busy === row.id}
                              onClick={() => decide(row.id, "reject")}
                              variant="outline"
                              className="rounded-full border-[#E5E5E5] text-crimson"
                              data-testid={`admin-pedigree-reject-${row.id}`}
                            >
                              <XCircle size={14} className="mr-1" weight="duotone" />
                              Reject
                            </Button>
                          </div>
                        )}
                        {row.status !== "pending_admin_approve" && (
                          <div className="shrink-0">
                            <Button
                              variant="outline"
                              onClick={() => setPreviewId(row.id)}
                              className="rounded-full border-[#C9A227]/40 text-[#8A6A00] bg-[#FBF7EC] hover:bg-[#F3E8C5]"
                              data-testid={`admin-pedigree-preview-${row.id}`}
                            >
                              <Eye size={14} className="mr-1" weight="duotone" />
                              Preview
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>
      <PedigreeAdditionPreview
        open={!!previewId}
        onClose={() => setPreviewId(null)}
        additionId={previewId}
      />
      <RequestRevisionsDialog
        open={!!revisionsTarget}
        onClose={() => setRevisionsTarget(null)}
        addition={revisionsTarget}
        onSent={() => {
          setRevisionsTarget(null);
          load();
        }}
      />
    </AppShell>
  );
}
